import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { jsPDF } from "jspdf";
import "jspdf-autotable"; // Import autoTable plugin
import React, { useEffect, useState, useCallback } from "react";
import { SlidingCubeLoader } from "react-loaders-kit";
import { useParams } from "react-router-dom";
import Toast from "../../../Components/Toast";
import useAuthStore from "../../../store/useAuthStore";
import useCompanyStore from "../../../store/useCompanyStore";

const Payslip = () => {
  const { payslipId } = useParams();
  const { token } = useAuthStore();
  const { companyInfo } = useCompanyStore();
  const companyName = companyInfo?.name || "Company";

  const [logoBase64, setLogoBase64] = useState(null);
  const [payslipData, setPayslipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", title: "", message: "" });

  // Pre-load company logo as base64 for PDF embedding
  useEffect(() => {
    if (!companyInfo?.logo) return;
    fetch(companyInfo.logo)
      .then((r) => r.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, [companyInfo?.logo]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  // Fetch payslip details on mount
  const fetchPayslipDetails = useCallback(async () => {
    try {
      const response = await fetch(`/api/payslip/${payslipId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        const allowances = data.payslip.allowances || [];
        const deductions = data.payslip.deductions || [];
        setEmailInput(data.payslip.email);
        setPayslipData({
          ...data.payslip,
          allowances: allowances.map((item) => ({ ...item, isEditing: false })),
          deductions: deductions.map((item) => ({ ...item, isEditing: false })),
        });
      } else {
        setToast({
          show: true,
          type: "error",
          title: "Error",
          message: data.error || "Failed to fetch payslip",
        });
      }
    } catch (error) {
      console.error("Error fetching payslip:", error);
      setToast({
        show: true,
        type: "error",
        title: "Error",
        message: "Error fetching payslip",
      });
    }
    setLoading(false);
  }, [payslipId, token]);

  useEffect(() => {
    fetchPayslipDetails();
  }, [fetchPayslipDetails]);

  // Log payslipData changes for debugging
  useEffect(() => {
    if (payslipData) {
      console.log("Current payslipData state:", {
        id: payslipData._id,
        employeeIdType: typeof payslipData.employeeId,
        employeeIdValue: payslipData.employeeId,
        hasEmployeeDetails: !!(payslipData.employeeId && typeof payslipData.employeeId === 'object' && payslipData.employeeId.name)
      });
    }
  }, [payslipData]);

  // --- Allowances Helpers ---
  const handleEditAllowance = (index) => {
    const newAllowances = [...payslipData.allowances];
    newAllowances[index].isEditing = true;
    setPayslipData({ ...payslipData, allowances: newAllowances });
  };

  const handleAllowanceChange = (index, field, value) => {
    const newAllowances = [...payslipData.allowances];
    newAllowances[index][field] = field === "amount" ? Number(value) : value;
    setPayslipData({ ...payslipData, allowances: newAllowances });
  };

  const handleSaveAllowance = (index) => {
    const newAllowances = [...payslipData.allowances];
    newAllowances[index].isEditing = false;
    setPayslipData({ ...payslipData, allowances: newAllowances });
  };

  const handleCancelAllowance = (index) => {
    const newAllowances = [...payslipData.allowances];
    if (!newAllowances[index]._id) {
      newAllowances.splice(index, 1);
    } else {
      newAllowances[index].isEditing = false;
    }
    setPayslipData({ ...payslipData, allowances: newAllowances });
  };

  const handleAddAllowance = () => {
    const newAllowance = { name: "", amount: 0, isEditing: true, new: true };
    setPayslipData({
      ...payslipData,
      allowances: [...payslipData.allowances, newAllowance],
    });
  };

  // --- Deductions Helpers ---
  const handleEditDeduction = (index) => {
    const newDeductions = [...payslipData.deductions];
    newDeductions[index].isEditing = true;
    setPayslipData({ ...payslipData, deductions: newDeductions });
  };

  const handleDeductionChange = (index, field, value) => {
    const newDeductions = [...payslipData.deductions];
    newDeductions[index][field] = field === "amount" ? Number(value) : value;
    setPayslipData({ ...payslipData, deductions: newDeductions });
  };

  const handleSaveDeduction = (index) => {
    const newDeductions = [...payslipData.deductions];
    newDeductions[index].isEditing = false;
    setPayslipData({ ...payslipData, deductions: newDeductions });
  };

  const handleCancelDeduction = (index) => {
    const newDeductions = [...payslipData.deductions];
    if (!newDeductions[index]._id) {
      newDeductions.splice(index, 1);
    } else {
      newDeductions[index].isEditing = false;
    }
    setPayslipData({ ...payslipData, deductions: newDeductions });
  };

  const handleAddDeduction = () => {
    const newDeduction = { name: "", amount: 0, isEditing: true, new: true };
    setPayslipData({
      ...payslipData,
      deductions: [...payslipData.deductions, newDeduction],
    });
  };

  // --- Status Change ---
  const handleStatusChange = (e) => {
    setPayslipData({ ...payslipData, status: e.target.value });
  };

  // --- Totals Calculation ---
  const basicSalary = payslipData?.basicSalary ?? 0;
  const allowancesTotal =
    payslipData?.allowances?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) ?? 0;
  const grossSalary = basicSalary + allowancesTotal;
  const totalDeductions =
    payslipData?.deductions?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) ?? 0;
  const netPay = grossSalary - totalDeductions;

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // --- PDF Generation (Modern Professional Template) ---
  const generatePDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageW = 595;
    const fmt = (amt) => `Nu. ${formatCurrency(amt)}`;

    // ── Header ──
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageW, 78, "F");
    doc.setFillColor(147, 197, 253);
    doc.rect(0, 78, pageW, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    if (logoBase64) {
      doc.addImage(logoBase64, "JPEG", 14, 10, 55, 55);
      doc.text(companyName.toUpperCase(), 78, 36);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("SALARY SLIP", 78, 56);
    } else {
      doc.text(companyName.toUpperCase(), 40, 36);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("SALARY SLIP", 40, 56);
    }

    const monthYear = new Date(payslipData.startDate).toLocaleString("default", { month: "long", year: "numeric" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(monthYear.toUpperCase(), pageW - 40, 36, { align: "right" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Period: ${new Date(payslipData.startDate).toLocaleDateString()} – ${new Date(payslipData.endDate).toLocaleDateString()}`,
      pageW - 40, 56, { align: "right" }
    );

    // ── Employee Info Bar ──
    doc.setFillColor(239, 246, 255);
    doc.rect(0, 82, pageW, 72, "F");

    const empName = (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.name : null) || payslipData.name || "N/A";
    const empId   = payslipData.empId || (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.employeeId : null) || "N/A";
    const dept    = payslipData.department || (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.department : null) || "N/A";
    const bank    = payslipData.bankDetails?.accountNumber || payslipData.bankDetails?.account || "N/A";

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("EMPLOYEE INFORMATION", 40, 99);

    const labelX = 40, valX = 130, labelX2 = 320, valX2 = 410;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);

    [
      ["Name", empName, "Department", dept, 115],
      ["Employee ID", empId, "Bank / Cheque", bank, 133],
    ].forEach(([l1, v1, l2, v2, y]) => {
      doc.setFont("helvetica", "bold"); doc.text(l1 + ":", labelX, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v1), valX, y);
      doc.setFont("helvetica", "bold"); doc.text(l2 + ":", labelX2, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v2), valX2, y);
    });

    // ── Earnings & Deductions Combined Table ──
    const pdfTaxable = payslipData.taxableSalary || Math.round(grossSalary * 0.85);
    const pdfTds = payslipData.tds ?? 0;

    const earningsRows = [
      ["Basic Salary", fmt(basicSalary)],
      ...payslipData.allowances.map((a) => [a.name, fmt(a.amount)]),
    ];
    const deductionRows = payslipData.deductions.map((d) => [d.name === "TDS" ? "TDS (Income Tax)" : d.name, fmt(d.amount)]);

    const maxLen = Math.max(earningsRows.length, deductionRows.length);
    while (earningsRows.length < maxLen) earningsRows.push(["", ""]);
    while (deductionRows.length < maxLen) deductionRows.push(["", ""]);

    const tableBody = earningsRows.map((e, i) => [e[0], e[1], deductionRows[i][0], deductionRows[i][1]]);
    // Total row
    tableBody.push(["Gross Salary", fmt(grossSalary), "Total Deductions", fmt(totalDeductions)]);

    doc.autoTable({
      startY: 170,
      head: [["EARNINGS", "AMOUNT", "DEDUCTIONS", "AMOUNT"]],
      body: tableBody,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [96, 165, 250], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 9 },
      columnStyles: {
        0: { halign: "left", cellWidth: 148 },
        1: { halign: "right", cellWidth: 110, textColor: [37, 99, 235] },
        2: { halign: "left", cellWidth: 148 },
        3: { halign: "right", cellWidth: 110, textColor: [220, 38, 38] },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = data.column.index < 2 ? [220, 252, 231] : [255, 237, 237];
          data.cell.styles.textColor = data.column.index < 2 ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.lineColor = [200, 210, 220];
        }
      },
      margin: { left: 27, right: 27 },
    });

    // ── Tax Summary ──
    const taxY = doc.lastAutoTable.finalY + 12;
    doc.autoTable({
      startY: taxY,
      head: [["Taxable Salary (85%)", "TDS / Income Tax"]],
      body: [[fmt(pdfTaxable), fmt(pdfTds)]],
      theme: "grid",
      styles: { fontSize: 9, halign: "center", cellPadding: 5, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [148, 163, 184], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 270 }, 1: { cellWidth: 270 } },
      margin: { left: 27, right: 27 },
    });

    // ── Net Pay Box ──
    const netY = doc.lastAutoTable.finalY + 14;
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(27, netY, pageW - 54, 46, 4, 4, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("NET PAY", 50, netY + 19);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Employee Net Pay", 50, netY + 33);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(fmt(netPay), pageW - 50, netY + 30, { align: "right" });

    // ── Footer ──
    doc.setDrawColor(226, 232, 240);
    doc.line(27, 795, pageW - 27, 795);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 163, 184);
    doc.text("This is a computer-generated document. No signature is required.", 27, 808);
    doc.text(`Generated by ${companyName} | ${new Date().toLocaleDateString()}`, pageW - 27, 808, { align: "right" });

    return doc;
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    // Include employee name in file name
    const employeeName = payslipData.employeeId?.name || "employee";
    doc.save(`payslip (${employeeName}).pdf`);
  };


  const handlePreviewEmail = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    setPdfPreviewUrl(blobUrl);
    // Use payslip.email (stored at generation), fallback to populated employeeId.email
    const recipientEmail =
      payslipData.email ||
      (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.email : "") ||
      "";
    setEmailInput(recipientEmail);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const doc = generatePDF();
      const pdfBase64 = doc.output("datauristring");
      const response = await fetch(`/api/send-payslip-email/${payslipId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: emailInput, pdf: pdfBase64 }),
      });
      const data = await response.json();
      if (response.ok) {
        setToast({
          show: true,
          type: "success",
          title: "Success",
          message: "Payslip emailed successfully!",
        });
        setShowEmailModal(false);
      } else {
        setToast({
          show: true,
          type: "error",
          title: "Error",
          message: data.error || "Email sending failed",
        });
      }
    } catch (error) {
      console.error("Send email error:", error);
      setToast({
        show: true,
        type: "error",
        title: "Error",
        message: "Email sending failed",
      });
    }
    setSendingEmail(false);
  };

  // --- Update Payslip ---
  const handleUpdatePayslip = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`/api/update-payslip/${payslipId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          allowances: payslipData.allowances.map(({ isEditing, new: _new, ...rest }) => rest),
          deductions: payslipData.deductions.map(({ isEditing, new: _new, ...rest }) => rest),
          status: payslipData.status,
        }),
      });
      const data = await response.json();
      console.log("Update response data:", JSON.stringify(data, null, 2));

      if (response.ok) {
        setToast({
          show: true,
          type: "success",
          title: "Success",
          message: "Payslip updated successfully",
        });

        // Use the updated payslip data from response
        const updatedPayslip = data.payslip;
        setPayslipData({
          ...updatedPayslip,
          allowances: (updatedPayslip.allowances || []).map((item) => ({ ...item, isEditing: false })),
          deductions: (updatedPayslip.deductions || []).map((item) => ({ ...item, isEditing: false })),
        });
      } else {
        setToast({
          show: true,
          type: "error",
          title: "Error",
          message: data.error || "Update failed",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      setToast({
        show: true,
        type: "error",
        title: "Error",
        message: "Update failed",
      });
    }
    setUpdating(false);
  };

  if (loading || !payslipData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SlidingCubeLoader loading={true} size={50} color="rgba(74, 144, 226, 1)" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-8">
        {/* Payslip Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
          <div className="mb-4 md:mb-0">
            <h1 className="text-3xl font-bold">Payslip</h1>
            <p className="text-gray-600">
              {new Date(payslipData.startDate).toLocaleDateString()} to{" "}
              {new Date(payslipData.endDate).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Download & Email Buttons */}
            <div className="bg-gray-100 flex w-40 h-10 border border-gray-300">
              <div
                onClick={handleDownloadPDF}
                className="flex-1 bg-gray-400 p-3 flex justify-center items-center cursor-pointer hover:bg-gray-600"
              >
                <DownloadIcon className="text-white" fontSize="small" />
              </div>
              <div
                onClick={handlePreviewEmail}
                className="flex-1 bg-white p-3 flex justify-center items-center rounded-r-lg cursor-pointer hover:bg-gray-100 border-l border-gray-300"
              >
                <SendIcon className="text-gray-700" fontSize="small" />
              </div>
            </div>
            {/* Status Selector */}
            <select
              value={payslipData.status}
              onChange={handleStatusChange}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Paid">Paid</option>
            </select>
            {/* Update Button */}
            <button
              onClick={handleUpdatePayslip}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={updating}
            >
              {updating ? "Updating..." : "Update Payslip"}
            </button>
          </div>
        </div>

        {/* Employee Details & Salary Summary (Responsive Row) */}
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          {/* Employee Details (Smaller) */}
          <div className="flex-1 bg-gray-100 p-4 rounded-lg border">
            <h2 className="text-lg font-semibold mb-2">Employee Details</h2>
            <div className="text-sm text-gray-600">
              <div className="flex">
                <span className="w-32 font-medium">Emp. ID</span>
                <span>: {
                  payslipData.empId ||
                  (typeof payslipData.employeeId === 'object' ? payslipData.employeeId?.employeeId : payslipData.employeeId) ||
                  "N/A"
                }</span>
              </div>
              <div className="flex">
                <span className="w-32 font-medium">Name</span>
                <span>: {
                  (typeof payslipData.employeeId === 'object' ? payslipData.employeeId?.name : payslipData.name) ||
                  payslipData.name ||
                  "N/A"
                }</span>
              </div>
              <div className="flex">
                <span className="w-32 font-medium">Department</span>
                <span>: {
                  payslipData.department ||
                  (typeof payslipData.employeeId === 'object' ? payslipData.employeeId?.department : null) ||
                  "N/A"
                }</span>
              </div>
              <div className="flex">
                <span className="w-32 font-medium">Bank/Cheque</span>
                <span>: {payslipData.bankDetails?.account || "N/A"}</span>
              </div>
            </div>
          </div>
          {/* Salary Summary */}
          <div className="w-full md:w-1/2 bg-white p-4 rounded-lg shadow border">
            <h2 className="text-lg font-semibold mb-2">Salary Summary</h2>
            <div className="text-sm text-gray-700">
              <div className="flex justify-between">
                <span>Basic Salary</span>
                <span>Nu. {formatCurrency(basicSalary)}</span>
              </div>
              
              {/* Detailed Allowances */}
              {payslipData?.allowances && payslipData.allowances.length > 0 ? (
                payslipData.allowances.map((item, index) => (
                  <div key={`allowance-${index}`} className="flex justify-between text-gray-600 pl-2 mt-1">
                    <span>+ {item.name}</span>
                    <span>Nu. {formatCurrency(item.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span>Total Allowances</span>
                  <span>Nu. {formatCurrency(allowancesTotal)}</span>
                </div>
              )}

              {/* Gross Salary */}
              <div className="flex justify-between font-semibold text-gray-800 border-t border-dashed border-gray-200 pt-1 mt-1">
                <span>Gross Salary</span>
                <span>Nu. {formatCurrency(grossSalary)}</span>
              </div>

              {/* Detailed Deductions */}
              {payslipData?.deductions && payslipData.deductions.length > 0 ? (
                payslipData.deductions.map((item, index) => (
                  <div key={`deduction-${index}`} className="flex justify-between text-gray-600 pl-2 mt-1">
                    <span>- {item.name} {item.name === "TDS" ? "(Income Tax)" : ""}</span>
                    <span>Nu. {formatCurrency(item.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span>Total Deductions</span>
                  <span>Nu. {formatCurrency(totalDeductions)}</span>
                </div>
              )}

              <div className="flex justify-between text-gray-500 mt-2 border-t pt-2">
                <span>Taxable Salary (85%)</span>
                <span>Nu. {formatCurrency(payslipData?.taxableSalary ? Math.round(payslipData.taxableSalary) : Math.round(grossSalary * 0.85))}</span>
              </div>
              <hr className="my-2" />
              <div className="bg-green-100 flex border-l-4 rounded-2xl border-green-500 text-2xl font-bold p-3 text-center">
                <p className="text-gray-900 text-2xl text-center mt-1">Nu. {formatCurrency(netPay)}</p>
                {/* <p className="text-gray-500 text-lg text-center mt-1">Employee Net Pay</p> */}
              </div>
            </div>
          </div>
        </div>

        {/* Allowances & Deductions Section */}
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          {/* Allowances */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Allowances</h2>
              <button onClick={handleAddAllowance} className="text-blue-500">
                <AddIcon />
              </button>
            </div>
            <div className="border rounded-lg overflow-hidden">
              {payslipData.allowances.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 border-b last:border-b-0">
                  {item.isEditing ? (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Name"
                          value={item.name}
                          onChange={(e) => handleAllowanceChange(index, "name", e.target.value)}
                          className="border p-1 rounded text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={item.amount}
                          onChange={(e) => handleAllowanceChange(index, "amount", e.target.value)}
                          className="border p-1 rounded text-sm w-20"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleSaveAllowance(index)} className="text-green-500 text-sm">
                          Save
                        </button>
                        <button onClick={() => handleCancelAllowance(index)} className="text-red-500 text-sm">
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <EditIcon
                          className="text-gray-400 cursor-pointer"
                          onClick={() => handleEditAllowance(index)}
                          fontSize="small"
                        />
                        <span>{item.name}</span>
                      </div>
                      <span>Nu. {formatCurrency(item.amount)}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Deductions */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Deductions</h2>
              <button onClick={handleAddDeduction} className="text-blue-500">
                <AddIcon />
              </button>
            </div>
            <div className="border rounded-lg overflow-hidden">
              {payslipData.deductions.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 border-b last:border-b-0">
                  {item.name === "TDS" ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm" title="TDS is auto-calculated">*</span>
                        <span>{item.name}</span>
                      </div>
                      <span>Nu. {formatCurrency(item.amount)}</span>
                    </>
                  ) : item.isEditing ? (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Name"
                          value={item.name}
                          onChange={(e) => handleDeductionChange(index, "name", e.target.value)}
                          className="border p-1 rounded text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={item.amount}
                          onChange={(e) => handleDeductionChange(index, "amount", e.target.value)}
                          className="border p-1 rounded text-sm w-20"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleSaveDeduction(index)} className="text-green-500 text-sm">
                          Save
                        </button>
                        <button onClick={() => handleCancelDeduction(index)} className="text-red-500 text-sm">
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <EditIcon
                          className="text-gray-400 cursor-pointer"
                          onClick={() => handleEditDeduction(index)}
                          fontSize="small"
                        />
                        <span>{item.name}</span>
                      </div>
                      <span>Nu. {formatCurrency(item.amount)}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Total Net Payable (Alternative Display) */}
        <div className="mt-8 p-4 bg-gray-800 text-white rounded-lg flex justify-between items-center">
          <span className="font-semibold">Total Net Payable</span>
          <span className="text-2xl font-bold">Nu. {formatCurrency(netPay)}</span>
        </div>

        {/* Email Preview Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 md:w-2/3 lg:w-1/2 relative">
              <h2 className="text-2xl font-bold mb-4">Email Payslip Preview</h2>
              <div className="mb-4">
                <label className="block font-medium mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="w-full border p-2 rounded"
                />
              </div>
              <div className="mb-4 border rounded">
                {pdfPreviewUrl ? (
                  <iframe
                    src={pdfPreviewUrl}
                    title="PDF Preview"
                    width="100%"
                    height="400px"
                    className="rounded"
                  />
                ) : (
                  <p className="p-4">Generating PDF preview...</p>
                )}
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  disabled={sendingEmail}
                >
                  {sendingEmail ? "Sending..." : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <Toast
            type={toast.type}
            title={toast.title}
            message={toast.message}
            duration={3000}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}
      </div>
    </div>
  );
};

export default Payslip;
